import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Shield, CheckCircle2, XCircle, RefreshCw, MessageSquare } from 'lucide-react';
import { useKvkkStatus } from '@/hooks/use-kvkk-status';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface KvkkConsentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phone: string;
  companyId: string;
  onConsentGranted: () => void;
}

export function KvkkConsentModal({
  open,
  onOpenChange,
  phone,
  companyId,
  onConsentGranted,
}: KvkkConsentModalProps) {
  const [sendingKvkkSms, setSendingKvkkSms] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { toast } = useToast();
  
  // Force refresh by temporarily changing phone to trigger hook re-fetch
  // We'll use a state that changes the phone prop passed to hook
  const [phoneForHook, setPhoneForHook] = useState(phone);
  
  // Update phoneForHook when phone prop changes
  useEffect(() => {
    console.log('📱 [KVKK Modal] Phone prop changed:', phone);
    setPhoneForHook(phone);
  }, [phone]);
  
  // Reset when modal opens
  useEffect(() => {
    if (open) {
      console.log('📱 [KVKK Modal] Modal opened with phone:', phone);
      setRefreshTrigger(0);
      setPhoneForHook(phone);
    }
  }, [open, phone]);
  
  // Manually refresh by temporarily changing the phone to force hook re-fetch
  const handleRefresh = async () => {
    // Temporarily change phone to empty, then back to original to force hook refresh
    setPhoneForHook('');
    setTimeout(() => {
      setPhoneForHook(phone);
      setRefreshTrigger(prev => prev + 1);
    }, 100);
  };
  
  // Use the hook with the dynamic phone
  const kvkkStatus = useKvkkStatus(phoneForHook);

  const sendKvkkSms = async () => {
    if (!companyId || !phone) {
      toast({
        title: 'Hata',
        description: 'Şirket ID veya telefon numarası bulunamadı',
        variant: 'destructive',
      });
      return;
    }

    setSendingKvkkSms(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('kvkk-send-sms', {
        body: {
          companyId,
          phone,
        },
      });

      if (error) {
        throw error;
      }

      toast({
        title: 'Başarılı',
        description: 'KVKK SMS başarıyla gönderildi. Müşteri onay verdiğinde durum otomatik güncellenecektir.',
      });
      
      // Refresh status after sending SMS (wait a bit for the SMS to be processed)
      setTimeout(() => {
        handleRefresh();
      }, 2000);
    } catch (err) {
      console.error('KVKK SMS error:', err);
      toast({
        title: 'Hata',
        description: err instanceof Error ? err.message : 'KVKK SMS gönderilemedi',
        variant: 'destructive',
      });
    } finally {
      setSendingKvkkSms(false);
    }
  };

  const handleFinish = () => {
    if (kvkkStatus.type === 'granted') {
      onConsentGranted();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            KVKK Onay Kontrolü
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-2">
              Telefon Numarası: <span className="font-medium">{phone}</span>
            </p>
          </div>

          {kvkkStatus.loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">KVKK durumu kontrol ediliyor...</span>
            </div>
          ) : (
            <>
              {/* Granted Status */}
              {kvkkStatus.type === 'granted' && (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-700">
                    ✅ KVKK onayı mevcut. Paket teklifi SMS'i gönderilebilir.
                  </AlertDescription>
                </Alert>
              )}

              {/* Denied Status */}
              {kvkkStatus.type === 'denied' && (
                <Alert className="border-red-200 bg-red-50">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-700">
                    ❌ KVKK onayı reddedilmiş. Müşteri tekrar onay verene kadar SMS gönderilemez.
                  </AlertDescription>
                </Alert>
              )}

              {/* Withdrawn or No Record Status */}
              {(kvkkStatus.type === 'withdrawn' || kvkkStatus.type === 'no_record') && (
                <Alert className="border-orange-200 bg-orange-50">
                  <Shield className="h-4 w-4 text-orange-600" />
                  <AlertDescription className="text-orange-700">
                    ℹ️ KVKK onayı bulunmuyor. Müşteriden onay almak için SMS gönderebilirsiniz.
                  </AlertDescription>
                </Alert>
              )}

              {/* Unknown Status */}
              {kvkkStatus.type === 'unknown' && (
                <Alert className="border-orange-200 bg-orange-50">
                  <Shield className="h-4 w-4 text-orange-600" />
                  <AlertDescription className="text-orange-700">
                    ℹ️ KVKK durumu kontrol edilemedi. Lütfen tekrar deneyin.
                  </AlertDescription>
                </Alert>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col gap-2">
                {/* Refresh Button - Always visible */}
                <Button
                  variant="outline"
                  onClick={handleRefresh}
                  disabled={kvkkStatus.loading}
                  className="w-full"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Durumu Yenile
                </Button>

                {/* Send KVKK SMS Button - Show when no consent */}
                {(kvkkStatus.type === 'no_record' || kvkkStatus.type === 'withdrawn' || kvkkStatus.type === 'unknown') && (
                  <Button
                    onClick={sendKvkkSms}
                    disabled={sendingKvkkSms || kvkkStatus.loading}
                    className="w-full"
                  >
                    {sendingKvkkSms ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        KVKK SMS Gönderiliyor...
                      </>
                    ) : (
                      <>
                        <MessageSquare className="w-4 h-4 mr-2" />
                        KVKK SMS Gönder
                      </>
                    )}
                  </Button>
                )}

                {/* Finish Button - Show when consent is granted */}
                {kvkkStatus.type === 'granted' && (
                  <Button
                    onClick={handleFinish}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Tamamla ve SMS Gönder
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

